import { readHomeChatMatchCache } from "./homeChatMatchPersistence";
import {
  matchProfileToWizardAnswers,
  persistMatchProfile,
  persistWizardAnswersFromChat,
} from "./matchScoreUtils";
import { searchParamsToWizardAnswers } from "./resolveProductMatch";

export const MATCH_INTENT_SOURCES = {
  AI_SESSION: "ai_session",
  MATCH_PROFILE: "match_profile",
  LOCAL_STORAGE: "local_storage",
  SHOPPER_MODEL: "shopper_model",
  NONE: "none",
};

/** Whether wizard-style answers carry enough signal to score or compare. */
export function hasMatchIntent(wizardAnswers) {
  if (!wizardAnswers || typeof wizardAnswers !== "object") return false;
  return Boolean(
    wizardAnswers.useCase ||
      wizardAnswers.type ||
      (wizardAnswers.mustHaves || []).length ||
      wizardAnswers.budget?.min != null ||
      wizardAnswers.budget?.max != null,
  );
}

/**
 * Single intent resolver for the whole app.
 * Priority: AI session searchParams → session matchProfile → localStorage → shopper model.
 */
export function resolveMatchIntent({ aiSession = null, user = null } = {}) {
  const userId = user?._id || user?.id || null;
  const session = aiSession || readHomeChatMatchCache(userId);

  const fromSearchParams = searchParamsToWizardAnswers(session?.searchParams);
  if (hasMatchIntent(fromSearchParams)) {
    return {
      wizardAnswers: fromSearchParams,
      source: MATCH_INTENT_SOURCES.AI_SESSION,
      aiSession: session,
    };
  }

  if (session?.matchProfile) {
    const fromProfile = matchProfileToWizardAnswers(session.matchProfile);
    if (hasMatchIntent(fromProfile)) {
      return {
        wizardAnswers: fromProfile,
        source: MATCH_INTENT_SOURCES.MATCH_PROFILE,
        aiSession: session,
      };
    }
  }

  try {
    const raw = localStorage.getItem("wizardAnswers");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (hasMatchIntent(parsed)) {
        return {
          wizardAnswers: parsed,
          source: MATCH_INTENT_SOURCES.LOCAL_STORAGE,
          aiSession: session,
        };
      }
    }
  } catch {
    /* ignore */
  }

  if (user?.id || user?._id) {
    return {
      wizardAnswers: {},
      source: MATCH_INTENT_SOURCES.SHOPPER_MODEL,
      aiSession: session,
    };
  }

  return {
    wizardAnswers: null,
    source: MATCH_INTENT_SOURCES.NONE,
    aiSession: session,
  };
}

/** Keep localStorage wizard answers aligned with the active feed match. */
export function syncWizardFromFeedMatch(match) {
  if (!match) return;

  const nextAnswers = match.searchParams
    ? searchParamsToWizardAnswers(match.searchParams)
    : match.matchProfile
      ? matchProfileToWizardAnswers(match.matchProfile)
      : null;

  if (!hasMatchIntent(nextAnswers)) return;

  try {
    const raw = localStorage.getItem("wizardAnswers");
    if (raw && JSON.stringify(JSON.parse(raw)) === JSON.stringify(nextAnswers)) {
      return;
    }
  } catch {
    /* ignore */
  }

  if (match.searchParams) {
    persistWizardAnswersFromChat(match.searchParams);
  } else if (match.matchProfile) {
    persistMatchProfile(match.matchProfile);
  }
}

export function formatBudgetRangeLabel(budget) {
  if (!budget || typeof budget !== "object") return null;
  const hasMin =
    budget.min != null && budget.min !== "" && Number(budget.min) > 0;
  const hasMax = budget.max != null && budget.max !== "";
  if (hasMin && hasMax) {
    return `$${Number(budget.min).toLocaleString()}–$${Number(budget.max).toLocaleString()}`;
  }
  if (hasMax) {
    return `up to $${Number(budget.max).toLocaleString()}`;
  }
  if (hasMin) {
    return `from $${Number(budget.min).toLocaleString()}`;
  }
  return null;
}

export default resolveMatchIntent;
